import { createSlice } from '@reduxjs/toolkit';
//
import { dispatch } from '../store';
// slice
import { updateError } from './user';
// utils
import { callApi } from '../../utils/apiCall';
import { hasFeatureAccess } from '../../utils/featurePermission';
import { CHECKLIST_COMPLETION_STATE, TASKS_V2_CHANGED_EVENT } from '../../constants/tasksV2';

// -----------------------------------------------

// Broadcast that a task/checklist item changed so detached listeners (tables, widgets,
// and per-task date columns) refetch without prop-drilling a refresh callback.
function emitTasksV2Changed() {
    if (typeof window !== 'undefined') window.dispatchEvent(new Event(TASKS_V2_CHANGED_EVENT));
}

const initialState = {
    isLoading: false,
    isCurrentTaskLoading: false,
    tasks: null,
    currentTask: null,
    summary: null,
    activity: null,
    comments: null,
};

const slice = createSlice({
    name: 'task',
    initialState,
    reducers: {
        reset(state) {
            Object.assign(state, initialState);
        },
        startLoading(state) {
            state.isLoading = true;
        },
        startCurrentTaskLoading(state) {
            state.isCurrentTaskLoading = true;
        },
        setTasks(state, action) {
            state.isLoading = false;
            state.tasks = action.payload;
        },
        setCurrentTask(state, action) {
            state.isLoading = false;
            state.isCurrentTaskLoading = false;
            state.currentTask = action.payload;
        },
        setSummary(state, action) {
            state.isLoading = false;
            state.summary = action.payload;
        },
        setActivity(state, action) {
            state.isLoading = false;
            state.activity = action.payload;
        },
        setComments(state, action) {
            state.isLoading = false;
            state.comments = action.payload;
        },
    }
});

export default slice.reducer;

export const { reset, startLoading, startCurrentTaskLoading, setTasks, setCurrentTask, setSummary, setActivity, setComments } = slice.actions;

// `tasks` is one global Redux slot, so only the latest list request may publish
// rows or errors. Cache busting starts a fresh HTTP request but cannot stop the
// older caller from resolving later; this generation closes that publish race.
let _latestTasksRequestId = 0;

export function getTasks(practiceId, params, options = {}) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        _latestTasksRequestId += 1;
        const requestId = _latestTasksRequestId;
        const isCurrentRequest = () => requestId === _latestTasksRequestId;
        try {
            dispatch(slice.actions.startLoading());
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/?${params}`, 'GET', null, false, options);
            if (!isCurrentRequest()) return;
            if (response.status === 200) {
                dispatch(slice.actions.setTasks(response.data));
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Something went wrong' }));
            }
        } catch (error) {
            if (!isCurrentRequest()) return;
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function getTask(practiceId, taskId) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            dispatch(slice.actions.startCurrentTaskLoading());
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/`);
            if (response.status === 200) {
                dispatch(slice.actions.setCurrentTask(response.data));
                return { ok: true };
            }
            if (response.status === 404) {
                dispatch(slice.actions.setCurrentTask(null));
                return { notFound: true };
            }
            dispatch(slice.actions.setCurrentTask(null));
            dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Something went wrong' }));
            return { error: true };
        } catch (err) {
            dispatch(slice.actions.setCurrentTask(null));
            dispatch(updateError({ success: false, message: err.message || 'Something went wrong' }));
            return { networkError: true };
        }
    };
}

export function createTask(practiceId, data, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/`, 'post', data);
            if (response.status === 201) {
                dispatch(updateError({ success: true, message: 'Task created successfully' }));
                handleSuccess?.({ success: true });
            } else {
                const message = response?.data?.detail || response?.data?.message || 'Something went wrong';
                dispatch(updateError({ success: false, message }));
                handleSuccess?.({ success: false, message });
            }
        } catch (error) {
            const message = error.message || 'Something went wrong';
            dispatch(updateError({ success: false, message }));
            handleSuccess?.({ success: false, message });
        }
    };
}

export function updateTask(practiceId, taskId, data, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/`, 'patch', data);
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Task updated successfully' }));
                handleSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function completeTask(practiceId, taskId, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/complete/`, 'post');
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Task completed successfully' }));
                emitTasksV2Changed();
                handleSuccess({ success: true });
            } else {
                const detail = response.data?.detail
                    || response.data?.non_field_errors?.[0]
                    || (typeof response.data === 'string' ? response.data : null)
                    || 'Could not complete task';
                dispatch(updateError({ success: false, message: detail }));
                handleSuccess({ success: false, message: detail });
            }
        } catch (error) {
            const detail = error.response?.data?.detail || error.message || 'Something went wrong';
            dispatch(updateError({ success: false, message: detail }));
            handleSuccess({ success: false, message: detail });
        }
    };
}

export function reopenTask(practiceId, taskId, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/reopen/`, 'post');
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Task reopened successfully' }));
                emitTasksV2Changed();
                if (handleSuccess) handleSuccess({ success: true });
            } else {
                const detail = response?.data?.detail || response?.data?.message || 'Something went wrong';
                dispatch(updateError({ success: false, message: detail }));
                if (handleSuccess) handleSuccess({ success: false, message: detail });
            }
        } catch (error) {
            const detail = error.message || 'Something went wrong';
            dispatch(updateError({ success: false, message: detail }));
            if (handleSuccess) handleSuccess({ success: false, message: detail });
        }
    };
}

export function archiveTask(practiceId, taskId, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/`, 'delete');
            if (response.status === 204 || response.status === 200) {
                dispatch(updateError({ success: true, message: 'Task archived successfully' }));
                emitTasksV2Changed();
                if (handleSuccess) handleSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Something went wrong' }));
                if (handleSuccess) handleSuccess({ success: false, message: response?.data?.detail || response?.data?.message || 'Something went wrong' });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
            if (handleSuccess) handleSuccess({ success: false, message: error.message || 'Something went wrong' });
        }
    };
}

// getTaskSummary — fetch KPI summary counts.
//
// `filterParams` is an optional URLSearchParams (or null) produced by
// buildTasksQueryParams(filters) in TaskDashboard. When provided, the summary
// endpoint receives the same filter subset that the board sends so the KPI
// numbers reflect the active filter set. Excluded: limit, offset, ordering,
// status__in (window counts are computed server-side per window).
// When absent (null / undefined) the summary returns unfiltered totals.
export function getTaskSummary(practiceId, filterParams) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const qs = filterParams && filterParams.toString() ? `?${filterParams.toString()}` : '';
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/my-summary/${qs}`);
            if (response.status === 200) {
                dispatch(slice.actions.setSummary(response.data));
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

// BE CONTRACT — completeChecklistItem
// POST /api/v1/practices/{p}/tasks/{taskId}/checklist/{itemId}/complete/
//
// Canonical body shape — mirrors ChecklistItemRow.jsx::useCompletionApi.postState (lines 479-490):
//   { completion_state: 'YES' }                                        — all item types
//   { completion_state: 'YES', response_payload: { resolved_action } } — LEAD_NEXT_ACTION only
//   { completion_state: 'SKIPPED', response_payload: { source: 'upnext_skip' } } — skip via Up Next card
//   { completion_state: 'SKIPPED', response_payload: { bulk_complete_origin: true } } — LEAD_NEXT_ACTION auto-skip (bulk complete, task #18)
//   { completion_state: 'SKIPPED', response_payload: {} } — manual bulk "Skip all" (FloatingBulkActionBar)
//     Discriminator: presence of bulk_complete_origin=true = auto-skip; absence = manual skip.
//     (Prior code sent { answer: 'manual' } on manual skip — not in the LEAD_NEXT_ACTION payload schema;
//      no FE reader checks this key, and it triggered a Sentry warn. Removed in task #18 cleanup.)
//
// LEAD_NEXT_ACTION items REQUIRE resolved_action nested inside response_payload.
// Missing resolved_action → 400 {"detail": "resolved_action is required for LEAD_NEXT_ACTION cells."}
//
// Valid resolved_action keys — driven by NextActionPanel.jsx OUTCOMES (BE has NO whitelist;
// any string is accepted, but these are the FE-defined canonical values):
//   treatment_start     — user intends to start treatment
//   clincheck           — ClinCheck review scheduled
//   follow_up           — follow-up contact recorded
//   treatment_complete  — treatment marked complete
//   archive             — lead archived (also fires V1 ArchivedLeadDashBoard thunk)
//
// Callers: AttendedGuidedChecklist.jsx handlePanelComplete + AttendedGuidedChecklistV2.jsx.
// NextActionPanel sets completedItem.resolvedAction which the orchestrator maps to
// { resolved_action } before calling this thunk as the `payload` arg.
//
// All other item types (EMAIL_SENT, PHONE_CALLED, etc.) → null payload → body = { completion_state }.
//
// completionState defaults to 'YES'. Pass 'SKIPPED' for the Up Next "Skip for now" flow.
// Success toast is suppressed for SKIPPED — the UI advances silently.
//
// handleSuccess is always called: { success: true } on 200, { success: false } on
// error / 4xx / 5xx — callers can reset optimistic state or show a retry affordance.
export function completeChecklistItem(practiceId, taskId, itemId, handleSuccess, payload = null, completionState = CHECKLIST_COMPLETION_STATE.YES) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            // Build body per canonical BE contract (ChecklistItemRow.jsx::useCompletionApi.postState)
            const body = { completion_state: completionState };
            if (payload) body.response_payload = payload;
            const response = await callApi(
                `/api/v1/practices/${practiceId}/tasks/${taskId}/checklist/${itemId}/complete/`,
                'post',
                body,
            );
            if (response.status === 200) {
                // Suppress toast for SKIPPED — callers advance UI silently.
                if (completionState !== CHECKLIST_COMPLETION_STATE.SKIPPED) {
                    dispatch(updateError({ success: true, message: 'Checklist item completed' }));
                }
                emitTasksV2Changed();
                if (handleSuccess) handleSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Something went wrong' }));
                if (handleSuccess) handleSuccess({ success: false });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
            if (handleSuccess) handleSuccess({ success: false });
        }
    };
}

export function reopenChecklistItem(practiceId, taskId, itemId, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/checklist/${itemId}/reopen/`, 'post');
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Checklist item reopened' }));
                emitTasksV2Changed();
                if (handleSuccess) handleSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Something went wrong' }));
                if (handleSuccess) handleSuccess({ success: false });
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
            if (handleSuccess) handleSuccess({ success: false });
        }
    };
}

export function addChecklistItem(practiceId, taskId, data, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/checklist/`, 'post', data);
            if (response.status === 201) {
                dispatch(updateError({ success: true, message: 'Checklist item added' }));
                if (handleSuccess) handleSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function addComment(practiceId, taskId, data, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/comments/`, 'post', data);
            if (response.status === 201) {
                dispatch(updateError({ success: true, message: 'Comment added' }));
                if (handleSuccess) handleSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function getActivity(practiceId, taskId) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/activity/`);
            if (response.status === 200) {
                dispatch(slice.actions.setActivity(response.data));
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

// Monotonic generation counter for the single global `comments` slot. Each
// getComments invocation claims the next generation; only the latest generation
// may mutate state. This prevents a slow task-A response (success OR error) from
// clobbering task-B after the user switches tasks. Monotonic — never reset.
let _latestCommentsRequestId = 0;

export function getComments(practiceId, taskId) {
    return async () => {
        // Claim the generation BEFORE the feature-access early return, so
        // switching to a task/practice that can't fetch still invalidates an
        // older in-flight response.
        _latestCommentsRequestId += 1;
        const requestId = _latestCommentsRequestId;
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            // This caller owns server-error presentation because it must first
            // prove the response still belongs to the latest task generation.
            // callApi's default 500/503 dispatch remains enabled everywhere
            // else; only this request defers it to the guarded branch below.
            const response = await callApi(
                `/api/v1/practices/${practiceId}/tasks/${taskId}/comments/`,
                'GET',
                null,
                false,
                { suppressServerErrorDispatch: true }
            );
            if (requestId !== _latestCommentsRequestId) return; // superseded → drop
            if (response.status === 200) {
                dispatch(slice.actions.setComments(response.data));
            } else {
                dispatch(updateError({
                    success: false,
                    status: response?.status,
                    message: response?.data?.detail || response?.data?.message || 'Something went wrong',
                }));
            }
        } catch (error) {
            if (requestId !== _latestCommentsRequestId) return; // stale error → drop
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function updateComment(practiceId, taskId, commentId, data, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/comments/${commentId}/`, 'patch', data);
            if (response.status === 200) {
                dispatch(updateError({ success: true, message: 'Comment updated' }));
                if (handleSuccess) handleSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function deleteComment(practiceId, taskId, commentId, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/comments/${commentId}/`, 'delete');
            if (response.status === 204 || response.status === 200) {
                dispatch(updateError({ success: true, message: 'Comment deleted' }));
                if (handleSuccess) handleSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}

export function pauseTask(practiceId, taskId, { resumeAt }, onSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/pause/`, 'post', { resume_at: resumeAt }); // eslint-disable-line camelcase
            if (response.status === 200) {
                if (onSuccess) onSuccess(response.data);
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Could not pause task' }));
            }
        } catch (err) {
            dispatch(updateError({ success: false, message: err.message || 'Could not pause task' }));
        }
    };
}

export function resumeTask(practiceId, taskId, onSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/${taskId}/resume/`, 'post');
            if (response.status === 200) {
                if (onSuccess) onSuccess(response.data);
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Could not resume task' }));
            }
        } catch (err) {
            dispatch(updateError({ success: false, message: err.message || 'Could not resume task' }));
        }
    };
}

// ────────────────────────────────────────────────────────────────────────────
// Lead-level cascade thunks
// ────────────────────────────────────────────────────────────────────────────

/**
 * Pause all checklist items for a lead (indefinitely — until resume-cascade).
 * POST /api/v1/practices/{p}/leads/{l}/pause-cascade/ { paused_until, lead_treatment? }
 *
 * ASSUMPTION: the backend requires a date string for paused_until (null may be
 * rejected). We use '9999-12-31T23:59:59Z' as an indefinite sentinel — the
 * resume-cascade endpoint is always used to unfreeze, never the expiry.
 *
 * NOTE: pause-cascade operates on checklist items. Standalone tasks (template=null,
 * no checklist items) are NOT paused by this endpoint — they remain OPEN in the
 * items list. "Complete all" (bulkCompleteTasks) covers standalone tasks.
 *
 * @param {number|null} options.leadTreatmentId - When provided, scopes the pause to
 *   that treatment's tasks only (treatment-scoped to avoid cross-treatment pause leak).
 *   Omit for lead-wide pause (e.g. bulk action bar).
 */
export function pauseLeadCascade(practiceId, leadId, { pausedUntil, leadTreatmentId }, onSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            // treatment-scoped to avoid cross-treatment pause leak when leadTreatmentId is set
            const body = { paused_until: pausedUntil }; // eslint-disable-line camelcase
            if (leadTreatmentId) body.lead_treatment = leadTreatmentId; // eslint-disable-line camelcase
            const response = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/pause-cascade/`,
                'post',
                body
            );
            if (response.status === 200 || response.status === 204) {
                if (onSuccess) onSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Could not pause tasks' }));
                if (onSuccess) onSuccess({ success: false });
            }
        } catch (err) {
            dispatch(updateError({ success: false, message: err.message || 'Could not pause tasks' }));
            if (onSuccess) onSuccess({ success: false });
        }
    };
}

/**
 * Resume all paused checklist items for a lead.
 * POST /api/v1/practices/{p}/leads/{l}/resume-cascade/ { lead_treatment? }
 * BE behaviour: shifts due_at forward by the paused duration per item.
 *
 * @param {object} [opts]
 * @param {number|null} [opts.leadTreatmentId] - When provided, scopes the resume to
 *   that treatment's tasks only (treatment-scoped to avoid cross-treatment resume leak).
 *   Omit for lead-wide resume (e.g. bulk action bar).
 */
export function resumeLeadCascade(practiceId, leadId, onSuccess, { leadTreatmentId } = {}) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            // treatment-scoped to avoid cross-treatment resume leak when leadTreatmentId is set
            const body = leadTreatmentId ? { lead_treatment: leadTreatmentId } : undefined; // eslint-disable-line camelcase
            const response = await callApi(
                `/api/v1/practices/${practiceId}/leads/${leadId}/resume-cascade/`,
                'post',
                body
            );
            if (response.status === 200 || response.status === 204) {
                if (onSuccess) onSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Could not resume tasks' }));
                if (onSuccess) onSuccess({ success: false });
            }
        } catch (err) {
            dispatch(updateError({ success: false, message: err.message || 'Could not resume tasks' }));
            if (onSuccess) onSuccess({ success: false });
        }
    };
}

/**
 * Complete a list of tasks individually using per-task POST /tasks/{id}/complete/.
 * Preferred over bulk-complete because the per-task endpoint is NOT in
 * _FRONT_MIRROR_BLOCKED_ACTIONS, so it works on front-mirror practices
 * (e.g. Test Practice 36) where POST /tasks/bulk-complete/ returns 409.
 * Runs all completions in parallel, then fires one toast and calls onSuccess.
 */
export function completeTasksInParallel(practiceId, taskIds, onSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        const ids = (taskIds || []).filter(Boolean);
        if (!ids.length) { if (onSuccess) onSuccess({ success: true }); return null; }
        try {
            const results = await Promise.all(
                ids.map((id) =>
                    callApi(
                        `/api/v1/practices/${practiceId}/tasks/${id}/complete/`,
                        'post',
                        { force: true }
                    )
                        .then((r) => ({ id, ok: r.status === 200 || r.status === 204 }))
                        .catch(() => ({ id, ok: false }))
                )
            );
            const succeeded = results.filter((r) => r.ok).length;
            const failed = results.length - succeeded;
            if (succeeded > 0) {
                dispatch(updateError({
                    success: true,
                    message: failed === 0
                        ? `Completed ${succeeded} task${succeeded !== 1 ? 's' : ''}`
                        : `Completed ${succeeded} of ${ids.length} task${ids.length !== 1 ? 's' : ''} (${failed} failed)`,
                }));
            } else {
                dispatch(updateError({ success: false, message: 'Could not complete tasks' }));
            }
            if (onSuccess) onSuccess({ success: succeeded > 0 });
        } catch (err) {
            dispatch(updateError({ success: false, message: err.message || 'Could not complete tasks' }));
            if (onSuccess) onSuccess({ success: false });
        }
    };
}

/**
 * Bulk-complete a list of tasks by ID.
 * POST /api/v1/practices/{p}/tasks/bulk-complete/ { task_ids, force: true }
 * NOTE: this endpoint is in _FRONT_MIRROR_BLOCKED_ACTIONS — it returns 409 on
 * front-mirror practices. Use completeTasksInParallel instead for user-facing flows.
 */
export function bulkCompleteTasks(practiceId, taskIds, onSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(
                `/api/v1/practices/${practiceId}/tasks/bulk-complete/`,
                'post',
                { task_ids: taskIds, force: true } // eslint-disable-line camelcase
            );
            if (response.status === 200 || response.status === 204) {
                if (onSuccess) onSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Could not complete tasks' }));
                if (onSuccess) onSuccess({ success: false });
            }
        } catch (err) {
            dispatch(updateError({ success: false, message: err.message || 'Could not complete tasks' }));
            if (onSuccess) onSuccess({ success: false });
        }
    };
}

export function createFromTemplate(practiceId, data, handleSuccess) {
    return async () => {
        if (!hasFeatureAccess('tasks-v2')) return null;
        try {
            const response = await callApi(`/api/v1/practices/${practiceId}/tasks/from-template/`, 'post', data);
            if (response.status === 201) {
                dispatch(updateError({ success: true, message: 'Task created from template' }));
                if (handleSuccess) handleSuccess({ success: true });
            } else {
                dispatch(updateError({ success: false, message: response?.data?.detail || response?.data?.message || 'Something went wrong' }));
            }
        } catch (error) {
            dispatch(updateError({ success: false, message: error.message || 'Something went wrong' }));
        }
    };
}
